/**
 * JobQueue Page
 * Main page for job queue management with modern Tailwind CSS design
 */

import React, { useState, useEffect } from 'react';
import { useJobQueue } from '../../hooks/useJobQueue';
import { Job, CreateJobPayload } from '../../types/job';
import { JobForm } from './JobForm';
import { JobList } from './JobList';
import { Plus, X, AlertCircle, CheckCircle, Info, Edit2, Trash2 } from 'lucide-react';

interface JobQueueProps {
  onNavigateToLogin?: () => void;
}

export const JobQueue: React.FC<JobQueueProps> = () => {
  const { jobs, loading, error, fetchJobs, createJob, updateJob, deleteJob, uploadFile } = useJobQueue();

  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    // Fetch jobs on component mount
    fetchJobs();
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleCreateJob = async (payload: CreateJobPayload | any) => {
    try {
      // Handle batch job creation (parent + multiple children)
      if (payload.isBatchJob && payload.childJobs) {
        console.log('Creating batch job with', payload.childJobs.length, 'children');
        
        // Create parent job first
        const parentPayload = {
          name: payload.name,
          templateId: payload.templateId,
          category: payload.category,
          description: payload.description,
          condition: payload.condition,
          inputFileId: payload.inputFileId,
          parameters: payload.parameters,
          scheduledFor: payload.scheduledFor,
          customCron: payload.customCron,
          isParentJob: true,
        };

        console.log('Creating parent job:', parentPayload);
        const parentResult = await createJob(parentPayload);
        if (!parentResult) {
          throw new Error('Failed to create parent job');
        }
        console.log('Parent job created:', parentResult);

        // Create all child jobs
        for (const childPayload of payload.childJobs) {
          console.log('Creating child job:', childPayload);
          const result = await createJob(childPayload);
          if (!result) {
            throw new Error(`Failed to create child job: ${childPayload.name}`);
          }
          console.log('Child job created:', result);
        }

        setSuccessMessage(`Job created successfully with ${payload.childJobs.length} tasks`);
        setShowForm(false);
        setEditingJob(null);
        await fetchJobs();
      } else {
        // Single job creation
        const result = await createJob(payload);
        if (result) {
          setSuccessMessage('Job created successfully');
          setShowForm(false);
          setEditingJob(null);
          await fetchJobs();
        } else {
          console.error('Job creation returned null');
        }
      }
    } catch (err) {
      console.error('Error in handleCreateJob:', err);
    }
  };

  const handleUpdateJob = async (payload: CreateJobPayload) => {
    if (editingJob) {
      const result = await updateJob(editingJob.id, {
        name: payload.name,
        description: payload.description,
        condition: payload.condition,
        parameters: payload.parameters,
        scheduledFor: payload.scheduledFor,
        customCron: payload.customCron,
      });
      if (result) {
        setSuccessMessage('Job updated successfully');
        setShowForm(false);
        setEditingJob(null);
        setSelectedJob(null);
        await fetchJobs();
      }
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    // Find the job to check if it's a parent job
    const jobToDelete = jobs.find(j => j.id === jobId);
    const isParentJob = (jobToDelete as any)?.isParentJob;
    
    // If it's a parent job, also delete all its children
    if (isParentJob) {
      const parentJobName = jobToDelete.name;
      const childJobs = jobs.filter(j => (j as any).isChildJob && (j as any).parentJobName === parentJobName);
      
      // Delete all child jobs first
      for (const child of childJobs) {
        await deleteJob(child.id);
      }
    }
    
    // Then delete the parent/standalone job
    const success = await deleteJob(jobId);
    if (success) {
      setSuccessMessage(isParentJob ? 'Parent job and all tasks deleted successfully' : 'Job deleted successfully');
      setSelectedJob(null);
      await fetchJobs();
    }
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    setShowForm(true);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingJob(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Job Queue</h1>
              <p className="mt-2 text-sm text-gray-600">Create, manage, and monitor your processing jobs</p>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                showForm
                  ? 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                  : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {showForm ? (
                <>
                  <X className="w-5 h-5" />
                  Cancel
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  New Job
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {successMessage && (
          <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading && !jobs.length && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">Loading job queue...</p>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          {showForm && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingJob ? 'Edit Job' : 'Create New Job'}
                  </h2>
                </div>
                <JobForm
                  job={editingJob || undefined}
                  onSubmit={editingJob ? handleUpdateJob : handleCreateJob}
                  onCancel={handleFormCancel}
                  onFileUpload={uploadFile}
                  loading={loading}
                />
              </div>
            </div>
          )}

          {/* List Section */}
          <div className={showForm ? 'lg:col-span-2' : 'lg:col-span-3'}>
            <JobList
              jobs={jobs}
              onEdit={handleEditJob}
              onDelete={handleDeleteJob}
              onSelect={setSelectedJob}
              selectedJobId={selectedJob?.id}
              loading={loading}
            />
          </div>
        </div>

        {/* Job Details Sidebar */}
        {selectedJob && !showForm && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => setSelectedJob(null)}
              aria-label="Close sidebar"
            />
            <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white border-l border-gray-200 shadow-lg overflow-y-auto z-40">
              <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 bg-white z-50">
                <h3 className="text-lg font-semibold text-gray-900">Job Details</h3>
                <button
                  type="button"
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                  title="Close sidebar"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

            <div className="p-6 space-y-6">
              {/* Job Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Name</label>
                <p className="text-base text-gray-900 font-medium">{selectedJob.name}</p>
              </div>

              {/* Status Badge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="flex gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    selectedJob.status === 'success'
                      ? 'bg-green-100 text-green-800'
                      : selectedJob.status === 'error'
                      ? 'bg-red-100 text-red-800'
                      : selectedJob.status === 'processing'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedJob.status.charAt(0).toUpperCase() + selectedJob.status.slice(1)}
                  </span>
                </div>
              </div>

              {/* Description */}
              {selectedJob.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-sm text-gray-600">{selectedJob.description}</p>
                </div>
              )}

              {/* Template and Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{selectedJob.templateId}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{selectedJob.category}</p>
                </div>
              </div>

              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded capitalize">{selectedJob.condition}</p>
              </div>

              {/* Input File */}
              {selectedJob.inputFile && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Input File</label>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                    <p className="text-sm font-medium text-gray-900">{selectedJob.inputFile.name}</p>
                    <p className="text-xs text-gray-600">{(selectedJob.inputFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              )}

              {/* Output File */}
              {selectedJob.outputFile && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Output File</label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-900">{selectedJob.outputFile.name}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4">
                {selectedJob.createdAt && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Created</label>
                    <p className="text-xs text-gray-900">{new Date(selectedJob.createdAt).toLocaleString()}</p>
                  </div>
                )}
                {selectedJob.completedAt && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Completed</label>
                    <p className="text-xs text-gray-900">{new Date(selectedJob.completedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {selectedJob.errorMessage && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <label className="block text-sm font-medium text-red-900 mb-2">Error Details</label>
                  <p className="text-sm text-red-700 whitespace-pre-wrap">{selectedJob.errorMessage}</p>
                </div>
              )}

              {/* Parameters */}
              {Object.keys(selectedJob.parameters).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Parameters</label>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-48 font-mono">
                    {JSON.stringify(selectedJob.parameters, null, 2)}
                  </pre>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    handleEditJob(selectedJob);
                    setSelectedJob(null);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm inline-flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleDeleteJob(selectedJob.id);
                    setSelectedJob(null);
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm inline-flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
